import { Button, Grid } from '@material-ui/core';
import { createFrontendPlugin, FrontendPlugin } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { compatWrapper } from '@backstage/core-compat-api';
import { Entity } from '@backstage/catalog-model';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  isResourceType,
} from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityPermissionsTab,
  TemplateAccessCard,
} from '@internal/plugin-rebac-explorer-frontend';
import { useRolloutFlag, PERMISSIONS_TAB_FLAG } from '../featureFlags';
import {
  EntityInfraWalletCard,
  isInfraWalletAvailable,
} from '@electrolux-oss/plugin-infrawallet';
import { EntityHasApisCard } from '@backstage/plugin-api-docs';
import {
  EntityStarredRatingsCard,
  StarredRatingButtons,
} from '@backstage-community/plugin-entity-feedback';
import { CiandtspikeDeleteEntityPage } from '@ciandtspike.io/plugin-ciandtspike-frontend-delete-entity';
import { CiandtspikeSecretsPage } from '@ciandtspike.io/plugin-ciandtspike-frontend-multicloud-secrets';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  CrossplaneAllResourcesTable,
  CrossplaneOverviewCard,
  CrossplaneResourceGraph,
  isCrossplaneAvailable,
} from '@terasky/backstage-plugin-crossplane-resources-frontend';

const isKindIn = (kinds: string[]) => (entity: Entity) =>
  kinds.includes(entity.kind.toLowerCase());

// Aba "Dependencies" para Component — no EntityPage legado era uma aba dedicada
// (não cards no Overview). Os cards default do catalog/alpha que apareceriam no
// Overview são desabilitados via app-config para evitar duplicação.
const DependenciesContent = () => (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityDependsOnComponentsCard />
    </Grid>
    <Grid item md={6}>
      <EntityDependsOnResourcesCard />
    </Grid>
  </Grid>
);

const dependenciesContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: isKindIn(['component']),
    loader: async () => compatWrapper(<DependenciesContent />),
  },
});

// Aba "CI/CD" para Component — placeholder herdado do EntityPage legado até
// existir integração de CI/CD por annotation.
const CicdContent = () => (
  <EmptyState
    title="No CI/CD available for this entity"
    missing="info"
    description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
    action={
      <Button
        variant="contained"
        color="primary"
        href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
      >
        Read more
      </Button>
    }
  />
);

const cicdContent = EntityContentBlueprint.make({
  name: 'cicd',
  params: {
    path: '/ci-cd',
    title: 'CI/CD',
    filter: isKindIn(['component']),
    loader: async () => compatWrapper(<CicdContent />),
  },
});

// Card de deleção exibido no Overview de System, Domain e resources crossplane
// (como no EntityPage legado). Usa type 'info' para ficar na coluna lateral junto
// ao About, em vez de espalhado entre os cards de conteúdo.
const canDeleteEntity = (entity: Entity) =>
  isKindIn(['system', 'domain'])(entity) || isCrossplaneAvailable(entity);

const deleteEntityCard = EntityCardBlueprint.make({
  name: 'ciandtspike-delete-entity',
  params: {
    type: 'info',
    filter: canDeleteEntity,
    loader: async () => compatWrapper(<CiandtspikeDeleteEntityPage />),
  },
});

// Card "APIs" (EntityHasApisCard) para System — no EntityPage legado o systemPage
// o exibia. O card has-apis do api-docs/alpha é restrito a Component, então o
// System precisa do card registrado à parte.
const systemApisCard = EntityCardBlueprint.make({
  name: 'system-has-apis',
  params: {
    type: 'content',
    filter: isKindIn(['system']),
    loader: async () => compatWrapper(<EntityHasApisCard />),
  },
});

// Aba "Diagram" — grafo de relações do catalog-graph (config do EntityPage legado:
// TOP_BOTTOM, bidirecional, relações de composição/dependência). O título do card
// varia por kind ("Resource Diagram" no crossplane, "System Diagram" no System).
const RelationsDiagram = ({ title }: { title: string }) => (
  <EntityCatalogGraphCard
    direction={Direction.TOP_BOTTOM}
    title={title}
    height={700}
    relations={[
      RELATION_PART_OF,
      RELATION_HAS_PART,
      RELATION_API_CONSUMED_BY,
      RELATION_API_PROVIDED_BY,
      RELATION_CONSUMES_API,
      RELATION_PROVIDES_API,
      RELATION_DEPENDENCY_OF,
      RELATION_DEPENDS_ON,
    ]}
    unidirectional={false}
  />
);

const makeDiagramContent = (
  name: string,
  cardTitle: string,
  filter: (entity: Entity) => boolean,
) =>
  EntityContentBlueprint.make({
    name,
    params: {
      path: '/diagram',
      title: 'Diagram',
      filter,
      loader: async () => compatWrapper(<RelationsDiagram title={cardTitle} />),
    },
  });

const crossplaneDiagramContent = makeDiagramContent(
  'crossplane-diagram',
  'Resource Diagram',
  isCrossplaneAvailable,
);

const systemDiagramContent = makeDiagramContent(
  'system-diagram',
  'System Diagram',
  isKindIn(['system']),
);

// Card "Crossplane Overview" — exibido no Overview de entities crossplane
// junto ao About Card, como no EntityPage legado (crossplaneEntityPage).
const crossplaneOverviewCard = EntityCardBlueprint.make({
  name: 'crossplane-overview',
  params: {
    type: 'info',
    filter: isCrossplaneAvailable,
    loader: async () => compatWrapper(<CrossplaneOverviewCard />),
  },
});

// Aba "Crossplane Resources" — tabela com todos os recursos managed pela entity.
const crossplaneResourcesContent = EntityContentBlueprint.make({
  name: 'crossplane-resources',
  params: {
    path: '/crossplane-resources',
    title: 'Crossplane Resources',
    filter: isCrossplaneAvailable,
    loader: async () => compatWrapper(<CrossplaneAllResourcesTable />),
  },
});

// Aba "Crossplane Graph" — grafo de recursos crossplane da entity.
const crossplaneGraphContent = EntityContentBlueprint.make({
  name: 'crossplane-graph',
  params: {
    path: '/crossplane-graph',
    title: 'Crossplane Graph',
    filter: isCrossplaneAvailable,
    loader: async () => compatWrapper(<CrossplaneResourceGraph />),
  },
});

// Aba "Entity Ratings" para Group e User. No DEV o rating vive em aba dedicada,
// não como card no Overview (o card ratings-table do feedback/alpha é desabilitado
// via app-config para esses kinds).
const entityRatingsContent = EntityContentBlueprint.make({
  name: 'entity-ratings',
  params: {
    path: '/entity-ratings',
    title: 'Entity Ratings',
    filter: isKindIn(['group', 'user']),
    loader: async () => compatWrapper(<EntityStarredRatingsCard />),
  },
});

// Card "Rate this entity" para Component e Template (como no overviewContent
// legado). O card ratings-buttons do feedback/alpha usa makeWithOverrides e ignora
// o filter de config, então é desabilitado no app-config e recriado aqui com filtro
// explícito e a variante starred (igual ao legado).
const RateEntityCard = () => (
  <InfoCard title="Rate this entity">
    <StarredRatingButtons requestResponseThreshold={3} />
  </InfoCard>
);

const rateEntityCard = EntityCardBlueprint.make({
  name: 'rate-entity',
  params: {
    type: 'content',
    filter: isKindIn(['component', 'template']),
    loader: async () => compatWrapper(<RateEntityCard />),
  },
});

// Aba "Costs" (InfraWallet) — só quando a entity tem a annotation do InfraWallet.
const infraWalletContent = EntityContentBlueprint.make({
  name: 'infrawallet-costs',
  params: {
    path: '/costs',
    title: 'Costs',
    filter: isInfraWalletAvailable,
    loader: async () => compatWrapper(<EntityInfraWalletCard />),
  },
});

// Aba "Secrets" — apenas para resources do tipo instance/cloudinstance.
const secretsContent = EntityContentBlueprint.make({
  name: 'ciandtspike-secrets',
  params: {
    path: '/secrets',
    title: 'Secrets',
    filter: isResourceType(['instance', 'cloudinstance']),
    loader: async () => compatWrapper(<CiandtspikeSecretsPage />),
  },
});

const KINDS_WITH_PERMISSIONS_TAB = ['component', 'api', 'group', 'user', 'system', 'domain'];

// Aba "Permissions" via slot do rebac-explorer-frontend. Gateada por
// PERMISSIONS_TAB_FLAG em runtime (flag kept — gates sidebar item + entity tab).
//
// O objeto FGA de toda entidade de catálogo é `resource:<kind>/<name>` — não
// `<kind>:<name>` — mesma convenção usada por assign-owner (scaffolder),
// pelo check de delete e pela limpeza de tuplas (fgaObjectId no
// ciandtspike-delete-entity-backend) e pelo botão "Manage access" do
// marketplace (ServiceDetailsPage's fgaCoords). Usar `entity.kind` como
// resourceType lia um objeto FGA diferente do que recebe a tupla de owner na
// criação, fazendo a aba mostrar a lista de grants sempre vazia.
const EntityPermissionsTabContent = () => {
  const { entity } = useEntity();
  const flagEnabled = useRolloutFlag(PERMISSIONS_TAB_FLAG);
  if (!flagEnabled) return null;
  return (
    <EntityPermissionsTab
      resourceType="resource"
      resourceId={`${entity.kind.toLowerCase()}/${entity.metadata.name}`}
    />
  );
};

const permissionsContent = EntityContentBlueprint.make({
  name: 'permissions',
  params: {
    path: '/permissions',
    title: 'Permissions',
    filter: (entity: Entity) =>
      KINDS_WITH_PERMISSIONS_TAB.includes(entity.kind.toLowerCase()),
    loader: async () => compatWrapper(<EntityPermissionsTabContent />),
  },
});

// Card "Access" no overview do Template — padrão diferente das demais kinds
// (card em vez de aba). Também gateado por PERMISSIONS_TAB_FLAG.
const TemplateAccessCardContent = () => {
  const { entity } = useEntity();
  const flagEnabled = useRolloutFlag(PERMISSIONS_TAB_FLAG);
  if (!flagEnabled) return null;
  return <TemplateAccessCard templateId={entity.metadata.name} />;
};

const templateAccessCard = EntityCardBlueprint.make({
  name: 'template-access',
  params: {
    type: 'info',
    filter: (entity: Entity) => entity.kind.toLowerCase() === 'template',
    loader: async () => compatWrapper(<TemplateAccessCardContent />),
  },
});

export const entityContentPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'app-entity-content',
  extensions: [
    deleteEntityCard,
    dependenciesContent,
    cicdContent,
    entityRatingsContent,
    infraWalletContent,
    secretsContent,
    systemApisCard,
    rateEntityCard,
    crossplaneDiagramContent,
    systemDiagramContent,
    crossplaneOverviewCard,
    crossplaneResourcesContent,
    crossplaneGraphContent,
    permissionsContent,
    templateAccessCard,
  ],
});

export default entityContentPlugin;
